<template>
  <div :class="[$style.root, { [$style.lpnh]: pageType === 'lpnh' }]">
    <PagePartsCartPartsProductHeader
      v-if="!minScreenLg"
      :product="productRef"
      :list-parts="listParts"
      :subtitle="subtitle"
      :gift-wrapping-label="giftWrappingLabel"
      :is-confirm="isConfirm"
      :is-embroidery-confirm="isEmbroideryConfirm"
      :page-type="pageType"
      :selected-gift="state.selectedGift"
      :already-favorite="!!state.favorite"
      :selected-sku="state.selectedSku"
      :select-embroidery="state.selectEmbroidery"
      :product-ref-id="productRefId"
      :product-segment-list="productSegmentList"
      @on-favorite="onFavorite"
      @fetch-product="fetchProduct"
      @update:product-ref-id="(v) => (productRefId = v)"
    />

    <PagePartsCartPartsCarousel
      :class="$style.top_image"
      :slide-images="slideImages"
      :gift-wrapped="isConfirm && !!state.selectedGift.sku_id"
      :is-gift-wrapping-available-image="
        (path: string) =>
          state.displayedSku?.main_image !== path &&
          (state.displayedSku?.sub_images.every((img) => img?.image_url !== path) ?? false)
        "
      @expand-clicked="openSkuImageModal"
    />

    <div :class="$style.detail">
      <PagePartsCartPartsProductHeader
        v-if="minScreenLg"
        :product="productRef"
        :list-parts="listParts"
        :subtitle="subtitle"
        :gift-wrapping-label="giftWrappingLabel"
        :is-confirm="isConfirm"
        :is-embroidery-confirm="isEmbroideryConfirm"
        :page-type="pageType"
        :selected-gift="state.selectedGift"
        :already-favorite="!!state.favorite"
        :selected-sku="state.selectedSku"
        :select-embroidery="state.selectEmbroidery"
        :product-ref-id="productRefId"
        :product-segment-list="productSegmentList"
        :class="$style.line_container"
        @on-favorite="onFavorite"
        @fetch-product="fetchProduct"
        @update:product-ref-id="(v) => (productRefId = v)"
      />
      <div :class="[$style.line_container, $style.color_with_size]">
        <div v-if="isDisplayRestockNotice" :class="$style.restock_notice">
          <p>再入荷のお知らせは、ご希望のカラーとサイズをタップしてご登録ください</p>
        </div>

        <ClientOnly>
          <PagePartsCartPartsSelectColor
            v-if="state.selectedSku && skusHasColor"
            :selected-sku="state.selectedSku"
            :skus="productRef.skus"
            :click-event="changeSelectedSku"
            :selected-color-label="selectedSkuColorLabel"
            :color-type="productRef.color_type ?? ''"
          />
        </ClientOnly>

        <div ref="sizeElementRef" :class="$style.size_container">
          <div :class="$style.label">
            <div :class="$style.left">
              <p>{{ selectedSkuSizeLabel }}</p>
              <p>{{ selectedSkuSize }}</p>
              <p v-show="selectedSkuShippingSupplement && !isOutOfStock(state.selectedSku)">
                （{{ selectedSkuShippingSupplement }}）
              </p>
            </div>
            <AtomsConsumerLink
              v-if="productRef.size_chart && (productRef.size_chart.pc_img_url || productRef.size_chart.sp_img_url)"
              :class="$style.link"
              @click="openSizeChartImageModal"
              >サイズチャートを見る</AtomsConsumerLink
            >
          </div>
          <p v-if="getRecommendSize">おすすめサイズ：{{ getRecommendSize }}</p>

          <PagePartsCartPartsSelectSize
            v-if="state.selectedSku"
            :selected-sku="state.selectedSku"
            :selected-size="state.selectedSize"
            :skus="productRef.skus"
            :click-event="changeSelectedSku"
            :is-error="!!state.selectedSizeErrorMessage"
          />
          <p v-if="!!state.selectedSizeErrorMessage" :class="[$style.error_message]">
            {{ state.selectedSizeErrorMessage }}
          </p>

          <p v-if="product.order_quantity_limit_type && product.order_quantity_limit">
            {{
              `※${
                product.order_quantity_limit_type === "order" ? "1回のご注文" : "商品1点（ラッピング袋・カードを除く）"
              }ごとに、${product.order_quantity_limit}個までご購入可能です`
            }}
          </p>
          <p v-if="product.is_not_individual_order">
            {{ `※この商品のみのご注文は承っておりません。他の商品と一緒にご購入ください` }}
          </p>
          <AtomsConsumerButton
            v-if="isBodygramSupportedRef || product.show_recommend_size"
            variant="secondary"
            block
            @click="openSizeCheck"
          >
            自分に合ったサイズを探す
          </AtomsConsumerButton>
          <AtomsConsumerLink v-if="stockInfoItem" :url="`/stock_info?category=${stockInfoItem.category_slug}`">
            <AtomsConsumerButton variant="secondary" block
              >{{ stockInfoItem.category_title }}シリーズの在庫状況</AtomsConsumerButton
            >
          </AtomsConsumerLink>
          <PagePartsDuvetCollectionBanner :is-duvet-collection="!!product.is_duvet_collection" />
          <!-- virtusize tag -->
          <div :class="`vs-placeholder-inpage-${cartIndex}`"></div>
        </div>
      </div>
      <div v-if="isOutOfStock(state.selectedSku)" :class="$style.out_of_stock_label">
        <p>在庫切れ</p>
        <p v-if="outOfStockSkuShippingSupplement">
          {{ outOfStockSkuShippingSupplement }}
        </p>
      </div>

      <div :class="[$style.line_container, $style.cart_button_container]">
        <ProductNoveltyEndText :product="productRef" />
        <div :class="$style.buttons">
          <CartAddCartButton
            v-if="state.selectedSku && pageType !== 'lpnh'"
            ref="addCartButtonRef"
            :product="productRef"
            :sku="state.selectedSku"
            :selected-size="state.selectedSize"
            :is-cart-not-addable="isBeforeReleaseDate(productRef)"
            :quantity="quantityRef"
            :gift="state.selectedGift"
            :is-confirm="isConfirm"
            :is-e-gift="store.eGift.is_select"
            :can-not-set-e-gift="canNotSetEGift"
            :is-embroidery-confirm="isEmbroideryConfirm"
            :embroidery="formatEmbroidery"
            :class-name="'_add-to-cart gtm_add_cart_btn_' + productRef.slug"
            @select-size-validation="selectSizeValidation"
          />
          <MoleculesConsumerFreeShippingText />
          <div
            v-show="
              !isOutOfStock(state.selectedSku) &&
              !productRef.is_not_individual_order &&
              !state.selectedSku?.is_reservation &&
              !isBeforeReleaseDate(productRef)
            "
            :class="$style.buy_now_with_amazon_pay"
          >
            <AtomsConsumerButton variant="primary" block :class="$style.buy_now_button" @click="onClickBuyNowBefore">
              今すぐ購入
            </AtomsConsumerButton>
            <div :class="$style.amazon_pay">
              <div
                :id="amazonPayButtonId"
                ref="amazonPayButtonRef"
                :class="{
                  [$style.disabled]:
                    (isCartEmpty && !state.selectedSize && !store.eGift.is_select) || showDuvetCollectionModal,
                }"
                @click.capture="onClickAmazonPay"
              ></div>
              <div
                v-show="isCartEmpty && !state.selectedSize && !store.eGift.is_select"
                :class="$style.cover"
                @click="selectSizeValidation"
              ></div>
              <div
                v-show="!(isCartEmpty && !state.selectedSize && !store.eGift.is_select) && showDuvetCollectionModal"
                :class="$style.cover"
                @click="isDuvetCollectionAmazonPayModalVisible = true"
              ></div>
            </div>
          </div>
        </div>
        <div v-if="description" :class="$style.description">
          <p v-html="sanitizeHtmlContent(description)"></p>
        </div>

        <div :class="[$style.line_container, $style.option]">
          <div :class="$style.stock_modal_button">
            <AtomsConsumerButton variant="secondary" block @click="openStoreStockModal"
              >店舗在庫を確認する</AtomsConsumerButton
            >
          </div>
          <AtomsConsumerButton
            v-if="!product.not_display_product_list && pageType !== 'lpnh'"
            :class="$style.fav"
            variant="secondary"
            @click="onFavorite"
          >
            <AtomsConsumerImage :src="state.favorite ? '/images/icon/fav-navy.svg' : '/images/icon/fav.svg'" />
          </AtomsConsumerButton>
        </div>

        <div
          v-if="
            deliveryLtPrefectures.prefectures.length &&
            deliveryDateLabel.length &&
            !isOutOfStock(state.selectedSku) &&
            !state.selectedSku?.is_reservation &&
            !isBeforeReleaseDate(productRef)
          "
          :class="[$style.line_container, $style.delivery_date_container]"
        >
          <AtomsConsumerImage src="/images/icon/delivery-icon.svg" :class="$style.delivery_icon" />
          <p v-if="isEmbroideryConfirm" :class="$style.tips"
            >刺繍加工のため、お届けまでに14日程度お時間をいただきます。</p
          >
          <div v-else :class="$style.select_container">
            <AtomsConsumerSelect v-model="state.selectedPrefecture" :options="PREFECTURES" :class="$style.select" />
            <MoleculesConsumerHeading :title="deliveryDateLabel" align="left" variant="sub" :title-font-size-px="16" />
            <p :class="$style.tips">※一部離島はご希望に添えない場合があります</p>
          </div>
        </div>

        <div
          v-if="!productRef.is_gift"
          v-show="!isOutOfStock(state.selectedSku) && !isBeforeReleaseDate(productRef)"
          :class="[$style.line_container, $style.option_container]"
        >
          <MoleculesConsumerHeading title="ギフト設定" align="left" variant="sub" :title-font-size-px="16" />
          <div :class="[$style.buttons, $style.wrapping_buttons]" class="wrapping_button_area">
            <!-- ギフトラッピング設定ボタン -->
            <template v-if="product.is_wrapping">
              <AtomsConsumerButton
                v-if="giftCards.length >= 1 && !isGiftCardAllOutOfStock"
                :variant="isConfirm ? 'primary_light' : 'secondary'"
                block
                rounded
                @click="openSelectGiftModal"
              >
                <span>メッセージカード：{{ isConfirm ? "設定済み" : "未設定" }}</span>
              </AtomsConsumerButton>
              <div v-else>
                <p>{{ giftWrappings.length === 0 ? "メッセージカード設定不可" : "メッセージカード在庫切れ" }}</p>
              </div>
            </template>
            <template v-else>
              <template v-if="giftWrappings.length >= 1 && !isGiftWrappingAllOutOfStock">
                <AtomsConsumerButton
                  :variant="isConfirm ? 'primary_light' : 'secondary'"
                  block
                  rounded
                  @click="openSelectGiftModal"
                >
                  {{
                    isConfirm
                      ? `${giftWrappingLabel}：設定済み`
                      : product.is_wrapping
                      ? "メッセージカード：未設定"
                      : "ギフトラッピング：未設定"
                  }}
                </AtomsConsumerButton>
              </template>
              <div v-else>
                <p>{{ giftWrappings.length === 0 ? "ラッピング不可" : "ラッピング在庫切れ" }}</p>
              </div>
            </template>

            <!-- eギフト、刺繍サービスボタン -->
            <AtomsConsumerButton
              v-if="eGiftConfigurable"
              :variant="store.eGift.is_select ? 'primary_light' : 'secondary'"
              block
              rounded
              :class="$style.e_gift_button"
              class="e_gift_button"
              @click="openEGiftModal"
            >
              {{ store.eGift.is_select ? "eギフト：設定済み" : "eギフトで贈る" }}
            </AtomsConsumerButton>
          </div>
          <AtomsConsumerButton
            v-if="embroideryConfigurable"
            :variant="isEmbroideryConfirm ? 'primary_light' : 'secondary'"
            :checked="isEmbroideryConfirm"
            block
            rounded
            @click="openEmbroidery"
          >
            刺繍サービス：{{ isEmbroideryConfirm ? "設定済み" : "未設定" }}
          </AtomsConsumerButton>

          <div
            v-if="eGiftConfigurable || product.is_embroidery || embroideryConfigurable || isProductInfoModal"
            :class="$style.tips"
          >
            <p v-if="eGiftConfigurable" :class="$style.row"
              >※eギフトの詳細は<AtomsConsumerLink @click="openEGiftExplanation">こちら</AtomsConsumerLink></p
            >
            <p v-else-if="product.is_embroidery && isEmbroideryConfirm" :class="$style.row">
              ※「eギフト」と「刺繍サービス」は同時に設定できません。刺繍サービスの設定を解除してからeギフトを設定してください。
            </p>

            <p v-if="embroideryConfigurable" :class="$style.row"
              >※刺繍サービスの詳細は<AtomsConsumerLink @click="openEmbroideryExplanation">こちら</AtomsConsumerLink></p
            >
            <p v-else-if="product.is_embroidery && store.eGift.is_select" :class="$style.row">
              ※eギフトと刺繍サービスは同時に設定できません。eギフトを解除してから刺繍サービスを設定してください。
            </p>
            <p
              v-else-if="product.is_embroidery && selectedGiftHasCardAndMessageAndAndBundlingInclude"
              :class="$style.row"
            >
              ※刺繍サービスをご希望の場合は「ラッピングして購入」オプションのみご利用いただけます。
            </p>
            <p v-if="isProductInfoModal" :class="$style.row">
              ※製品情報は<AtomsConsumerLink @click="openProductInfoModal">こちら</AtomsConsumerLink>
            </p>
          </div>
        </div>
      </div>
    </div>

    <MoleculesConsumerModal v-model="modalState.topImage.show" :class="$style.top_image_modal" min-height="100%">
      <div :class="$style.image_modal">
        <video
          v-if="isVideo(modalState.topImage.path)"
          autoplay
          muted
          loop
          playsinline
          :src="modalState.topImage.path"
        ></video>
        <AtomsConsumerImage
          v-else
          :class="[$style.img, { [$style.zoom]: modalState.topImage.isZoom }]"
          :src="modalState.topImage.path"
          :quality="0.85"
          @click="modalState.topImage.isZoom = !modalState.topImage.isZoom"
        />
      </div>
    </MoleculesConsumerModal>

    <MoleculesConsumerModal v-model="modalState.displayStoreStock">
      <PagePartsCartPartsDisplayStock
        :selected-sku="state.selectedDisplayStockSku"
        :stores-detail="state.storesDetail"
        :product="productRef"
        :is-set-item="isSetItemRef"
        @enter="enter"
        @leave="leave"
        @after-enter="afterEnter"
        @after-leave="afterLeave"
        @update:selected-sku="changeDisplayStockSelectedSku"
      />
    </MoleculesConsumerModal>

    <MoleculesConsumerModal v-model="modalState.sizeChart">
      <div :class="$style.image_modal">
        <AtomsConsumerImage
          v-if="minScreenLg && productRef?.size_chart?.pc_img_url"
          :src="productRef.size_chart.pc_img_url"
        />
        <AtomsConsumerImage
          v-if="!minScreenLg && productRef?.size_chart?.sp_img_url"
          :src="productRef.size_chart.sp_img_url"
        />
      </div>
    </MoleculesConsumerModal>

    <PagePartsCartPartsModal ref="siteCartModalRef">
      <PagePartsCartPartsSettingGift
        v-show="displayGiftRef"
        :gift-wrappings="giftWrappings"
        :gift-messages="giftMessages"
        :gift-cards="giftCards"
        :is-card-only="productRef.is_wrapping"
        :is-embroidery-confirm="isEmbroideryConfirm"
        :is-embroidery-item="productRef.is_embroidery"
        :is-from-embroidery="isFromEmbroidery"
        :is-embroidery-products-in-cart="isEmbroidery"
        @confirm="wrappingConfirm"
        @cancel="cancelGiftModal"
      />
      <PagePartsCartPartsSettingEGift
        v-model:message-value="store.eGift.message"
        v-model:name-value="store.eGift.giver_name"
        v-model:is-anonymous="store.eGift.isAnonymous"
        :sku-is-out-of-stock="skusIsOutOfStock"
        :is-confirm="isConfirm"
        :display-e-gift-ref="displayEGiftRef"
        @e-gift-confirm="eGiftConfirm"
        @e-gift-lift="eGiftLift"
        @open-gift-modal="openGiftModal"
      />
      <PagePartsCartPartsEGiftAnnotation
        :display-explanation-ref="displayEGiftExplanationRef"
        @e-gift-contents-close="eGiftContentsClose"
      />
      <PagePartsCartPartsSelectEmbroidery
        v-show="displayEmbroideryRef"
        v-model:text-value="state.selectEmbroidery.text"
        v-model:font-value="state.selectEmbroidery.font"
        v-model:color-value="state.selectEmbroidery.color"
        v-model:position-value="state.selectEmbroidery.position"
        :positions="productRef?.embroidery?.embroidery_positions ?? []"
        :position-img="productRef?.embroidery?.embroidery_position_img ?? ''"
        :price="state.selectEmbroidery.price"
        :is-confirm="isConfirm"
        @embroidery-canceled="embroideryCanceled"
        @open-gift-modal="openGiftModal"
        @confirm="embroideryConfirm"
      />
      <PagePartsCartPartsEmbroideryAnnotation
        v-show="displayEmbroideryExplanationRef"
        :display-explanation-ref="displayEmbroideryExplanationRef"
        @close="embroideryContentsClose"
      />
      <PagePartsCartPartsSizeCheck
        v-show="displaySizeCheckRef"
        :size-chart="state.sizeChart"
        :product-id="product.document_id"
        :product-name="product.name"
        :recommend-size="getRecommendSize"
        :recommend-col-index="recommendColIndex"
        :recommend-row-index="recommendRowIndex"
        :is-foot-category="isFootCategory"
        :is-loading="isSizeCheckLoading"
        @check="sizeCheckContentsOpen"
        @close="sizeCheckContentsClose"
      />
    </PagePartsCartPartsModal>

    <CommonModalRestockNotice
      v-if="state.selectedSku && state.selectedSku.is_restock_notice"
      :sku="state.selectedSku"
      :product="productRef"
    />

    <!-- 羽毛布団回収に関するモーダル 内容は同じなので一つのコンポーネントで共通に使ってもいいが、わかりやすいように分けた-->
    <OrderDuvetCollectionModal
      v-model:visible="isDuvetCollectionBuyNowModalVisible"
      @cancel="isDuvetCollectionBuyNowModalVisible = false"
      @done="onClickBuyNow"
    />
    <OrderDuvetCollectionModal
      v-model:visible="isDuvetCollectionAmazonPayModalVisible"
      @cancel="isDuvetCollectionAmazonPayModalVisible = false"
      @done="onAmazonPayDuvetCollectionModalDone"
    />
  </div>
</template>

<script setup lang="ts">
import dayjs from "dayjs"
import { CartGift } from "@tential/ec-gql-schema/models/order"
import { calcPrice } from "@tential/ec-gql-schema/utils/functions/price"
import { sanitizeHtmlContent } from "@tential/ec-gql-schema/utils/functions/sanitize"
import { isEmbroideryProductsInCart } from "@tential/ec-gql-schema/utils/embroidery"
import { isVideo } from "@tential/ec-gql-schema/utils/functions/common"
import { SELECT_GIFT_INIT_DATA, PRODUCT_IDS_IN_STOCK_INFO } from "~/const/product"
import { PagePartsProductType } from "~/types/product"
import CartModal from "~/components/pageParts/cartParts/Modal.vue"
import {
  PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery,
  SkuFragmentFragment,
  PublicProductFindOneQueryVariables,
} from "~/types/type.generated"
import { EventHandler } from "~/types/events"
import { defineRestockNoticeModal, useToast } from "~/state"
import { useStore } from "~/stores"
import { EmbroiderySelect } from "~/types/embroidery"
import { usePublicProductFindOneQuery } from "~/gql/urql.generated"
import { useCart } from "~/composables/useCart"
import { useDuvetCollection } from "~/composables/useDuvetCollection"
import AmazonPayClient from "~/pageScripts/amazonpay"
import { PREFECTURES } from "~/const"
import { StoreStockAndArea } from "~/const/shop"
import { isBeforeReleaseDate } from "~/utils/functions/product"
import { useFavorite } from "~/composables/useFavorite"
import CartAddCartButton from "~/components/cart/AddCartButton.vue"
import type {
  RecommendSizeChartGender,
  RecommendSizeChartResponse,
  RecommendSizeCharFootSize,
} from "~/types/server/api/recommend/size_chart"
import { PagePartsFragmentFragment } from "~/types/pageParts"
import type { ApiDataStockQuery, ApiDataStockResponse } from "~/server/api/data/stock/index.get"
import { EMBROIDERY_UNIT_PRICE } from "~/const/cart"
import { useSizeCheck } from "~/composables/useSizeCheck"
import type { ApiDataFavoriteProduct } from "~/types/server/api/data/favorite"
import { Body2FitWidgetInstance } from "~/types/bodygram"
import { useBody2Fit } from "~/composables/useBody2Fit"

interface Props {
  product: PagePartsProductType
  listParts: PagePartsFragmentFragment["list"]
  index?: number
  cartIndex?: number
  subtitle: string
  description: string
  deliveryLtPrefectures: PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery["publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures"]
  pageType?: string
  isProductInfoModal?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  cartIndex: 0,
  pageType: "",
})

const emit = defineEmits<{
  (e: "openProductInfoModal"): void
}>()

const addCartButtonRef = ref<InstanceType<typeof CartAddCartButton>>()
const amazonPayButtonRef = ref<HTMLDivElement>()
const topImagePath = ref<string>("")

const { filterIndividualGiftWrappings, filterIndividualMessages, filterIndividualCards, fetchGiftPattern } = useGift()
const { trackSizeRecommendClick } = useGtagEvent()
const { fetchRecommendSizeChart } = useSizeCheck()
const { initBody2FitWidget } = useBody2Fit()
const { visitStore } = useStoreOrder()

type State = {
  selectedSku: SkuFragmentFragment | null
  selectedDisplayStockSku: SkuFragmentFragment | null
  displayedSku: SkuFragmentFragment | null
  isSp: boolean
  selectedSize: string
  selectedSizeErrorMessage: string
  selectedGift: CartGift
  selectedPrefecture: string
  storesDetail: StoreStockAndArea[]
  selectEmbroidery: EmbroiderySelect
  favorite: ApiDataFavoriteProduct | undefined
  sizeChart: RecommendSizeChartResponse
}

const state = reactive<State>({
  selectedSku: null,
  selectedDisplayStockSku: null,
  displayedSku: null,
  isSp: false,
  selectedSize: "",
  selectedSizeErrorMessage: "",
  selectedGift: SELECT_GIFT_INIT_DATA,
  selectedPrefecture: props.deliveryLtPrefectures.default_prefecture,
  storesDetail:
    PREFECTURES.map((prefecture) => ({
      prefecture,
      stocks: [],
      isOpen: true,
      filteredStocks: [],
      filteredSetStocks: [],
    })) || [],
  selectEmbroidery: {
    text: "",
    font: "英数字ゴシック体",
    color: "シルバー",
    position: "",
    price: EMBROIDERY_UNIT_PRICE,
  },
  favorite: undefined,
  sizeChart: {
    heights: [],
    weights: [],
    apparelRecommendSizes: [],
    footRecommendSizes: [],
    footSizes: [],
  },
})
const sizeElementRef = ref<HTMLElement>()

// 在庫切れで再入荷通知がONのSKUがあるか
const isDisplayRestockNotice = computed(() => {
  return productRef.value?.skus.some((sku) => sku.is_restock_notice && isSkuOutOfStock(sku))
})

const isFootCategory = props.product.product_categories.some((category) => category.slug === "sandal")

const deliveryDateLabel = computed<string>(() => {
  const deliveryLt = props.deliveryLtPrefectures.prefectures.find(
    (record) => record?.prefecture === state.selectedPrefecture,
  )
  return deliveryLt ? `お届け：${dayjs(deliveryLt.delivery_date).locale("ja").format("M月D日(ddd)")} 予定` : ""
})

const formatEmbroidery = computed(() => ({
  text: state.selectEmbroidery.text,
  font: state.selectEmbroidery.font,
  color: state.selectEmbroidery.color,
  position: state.selectEmbroidery.position,
  price: state.selectEmbroidery.price,
}))

const selectedGiftHasCardAndMessageAndAndBundlingInclude = computed(() => {
  const isBundledWrapping = store.cartList.some((cart) => cart.is_bundled_wrapping)
  return state.selectedGift.card_sku_id || state.selectedGift.message_sku_id || isBundledWrapping
})

/**
 * サイズチャートの推奨サイズの行列のインデックス
 */
const recommendRowIndex = 1
const recommendColIndex = 1
const getRecommendSize = computed(() => {
  let size = ""

  if (isFootCategory) {
    size = state.sizeChart.footRecommendSizes[recommendColIndex]
  } else {
    const row = state.sizeChart.apparelRecommendSizes[recommendRowIndex]
    size = row ? row[recommendColIndex] : ""
  }
  return size && size !== "-" ? size : ""
})

const callAddCart = async (): Promise<void> => {
  if (addCartButtonRef.value) await addCartButtonRef.value.addCart()
}
defineExpose({
  callAddCart,
})

const toast = useToast()
const { addCartMultiItem } = useCart()
const { showDuvetCollection, getDuvetCollectionType } = useDuvetCollection()
const activeImageElement = ref<HTMLDivElement>()
const siteCartModalRef = ref<InstanceType<typeof CartModal>>()
const productRefId = ref<string>(props.product.document_id)
const productRef = ref<PagePartsProductType>(props.product)
const quantityRef = ref<number>(1)
const displayGiftRef = ref<boolean>(false)
const isConfirm = ref<boolean>(false)
const isEmbroideryConfirm = ref<boolean>(false)
const displayEGiftRef = ref<boolean>(false)
const displayEGiftExplanationRef = ref<boolean>(false)
const displayEmbroideryRef = ref<boolean>(false)
const displayEmbroideryExplanationRef = ref<boolean>(false)
const displaySizeCheckRef = ref<boolean>(false)
const isFromEGift = ref<boolean>(false)
const isFromEmbroidery = ref<boolean>(false)
const router = useRouter()
const config = useRuntimeConfig()
const store = useStore()
const { isMobile } = useDevice()
const isSetItemRef = ref<boolean>(false)
const { signInCheck, onFavoriteByCart, favoriteListRef, fetchFavoriteListByCart, isSigned } = useFavorite()
const isDuvetCollectionBuyNowModalVisible = ref<boolean>(false)
const isDuvetCollectionAmazonPayModalVisible = ref<boolean>(false)
const isSizeCheckLoading = ref<boolean>(false)
const bodygramWidgetRef = ref<Body2FitWidgetInstance>()
const isBodygramSupportedRef = ref(false)

type ModalState = {
  topImage: {
    show: boolean
    path: string
    isZoom: boolean
  }
  displayStoreStock: boolean
  sizeChart: boolean
}
/**
 * MoleculesConsumerModal を利用したモーダルの表示切り替えと状態を保持する
 */
const modalState = ref<ModalState>({
  topImage: {
    show: false,
    path: "",
    isZoom: true,
  },
  displayStoreStock: false,
  sizeChart: false,
})

const { width: windowWidth } = useWindowSize()
// @mixin lg と同じ値に設定
const minScreenLg = computed(() => windowWidth.value >= 1024)

const stockInfoItem = computed(() => {
  return PRODUCT_IDS_IN_STOCK_INFO.find((item) => item.productIds.includes(props.product.document_id))
})

const carouselStyle = ref<{ height: `${number}px` }>()
useResizeObserver(activeImageElement, (entries) => {
  if (!minScreenLg.value) {
    carouselStyle.value = undefined
    return
  }

  const entry = entries[0]
  const { height } = entry.contentRect
  carouselStyle.value = {
    height: `${height}px`,
  }
})

const productVariablesRef = ref<PublicProductFindOneQueryVariables>({
  filter: {
    is_status: true,
  },
})

const { data: publicProductFindOneQueryRet, executeQuery: publicProductFindOneQueryExec } =
  await usePublicProductFindOneQuery({
    variables: productVariablesRef.value,
    requestPolicy: "network-only",
    pause: true,
  })

const fetchProduct = async (): Promise<void> => {
  productVariablesRef.value.filter = { document_id: productRefId.value }
  await nextTick(publicProductFindOneQueryExec)
  productRef.value = publicProductFindOneQueryRet.value?.publicProductFindOne || productRef.value

  const inStockSkus = productRef.value.skus.filter((sku) => !isSkuOutOfStock(sku))
  const selectedSku = inStockSkus[0] ?? productRef.value.skus[0] ?? null

  state.selectedSku = selectedSku
  state.displayedSku = selectedSku
  state.selectedSize = inStockSkus.length === 1 ? selectedSku?.size || "" : ""
  state.selectedSizeErrorMessage = ""

  embroideryCanceled(true)
  state.selectEmbroidery.position = productRef.value.embroidery?.embroidery_positions[0] ?? ""
  isSetItemRef.value = productRef.value?.wear_category === "set"
}

const initializationStocks = (stocks: ApiDataStockResponse, storesDetail: StoreStockAndArea[]) => {
  stocks.forEach((stock) => {
    if (stock.store && stock.store.prefecture) {
      const storeData = storesDetail.find((s) => s.prefecture === stock.store?.prefecture)
      if (storeData) {
        storeData.stocks.push(stock)
      }
    }
  })
  updateFilteredStocks()
}

const isEmbroidery = computed(() => isEmbroideryProductsInCart(store.cartList))

const canNotSetEGift = computed(() => {
  return giftWrappings.value.length === 0 || state.selectedSku?.is_reservation
})

const amazonPayButtonId = `AmazonPayButton${props.index ?? ""}`
defineRestockNoticeModal()

const giftMessages = computed(() => {
  return filterIndividualMessages(productRef.value ? productRef.value.document_id : "", state.selectedGift.sku_id)
})

const giftCards = computed(() => {
  return filterIndividualCards(productRef.value ? productRef.value.document_id : "", state.selectedGift.card_sku_id)
})

const giftWrappingLabel = computed(() => {
  if (state.selectedGift.sku_id && state.selectedGift.card_sku_id) {
    return "ギフトラッピング・カード"
  } else if (state.selectedGift.sku_id && !state.selectedGift.card_sku_id) {
    return "ギフトラッピング"
  } else if (!state.selectedGift.sku_id && state.selectedGift.card_sku_id) {
    return "メッセージカード"
  } else {
    return ""
  }
})

const skusHasColor = computed(() => {
  return productRef.value?.skus?.some((sku) => typeof sku.color === "string" && sku.color !== "")
})

const giftWrappings = computed(() => {
  return filterIndividualGiftWrappings(
    productRef.value ? productRef.value.document_id : "",
    state.selectedGift.sku_id || null,
    state.selectedSku,
  )
})

// ラッピング資材画像
const giftWrappingImageIndex = computed(() => {
  return state.displayedSku?.sub_images?.findIndex((img) => img?.is_wrapping) || -1
})

// ラッピング資材が全て在庫切れか
const isGiftWrappingAllOutOfStock = computed(() => {
  return giftWrappings.value.every((wrapping) => wrapping.isOutOfStock)
})

// メッセージカードが全て在庫切れか
const isGiftCardAllOutOfStock = computed(() => {
  return giftCards.value.every((card) => card.isOutOfStock)
})

// カートが空か
const isCartEmpty = computed(() => store.cartList.length === 0)

const cancelGiftModal = (): void => {
  isConfirm.value = false
  state.selectedGift = SELECT_GIFT_INIT_DATA
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const embroideryCanceled = (isReset: boolean): void => {
  isEmbroideryConfirm.value = false
  state.selectEmbroidery.text = ""
  state.selectEmbroidery.font = "英数字ゴシック体"
  state.selectEmbroidery.color = "シルバー"
  state.selectEmbroidery.price = EMBROIDERY_UNIT_PRICE
  if (isReset) return
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const embroideryConfirm = (): void => {
  if (store.cartList.some((cart) => !cart.embroidery)) {
    toast?.showErrorToasted(
      "通常商品が入っている場合は刺繍サービスを設定できません。追加する場合は通常商品をカートから削除してください",
    )
    return
  }
  isEmbroideryConfirm.value = true
  toast?.showSuccessToasted("刺繍サービスの設定を完了しました")
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
  if (isMobile) ScrollToAddCartBtn()
}

const ScrollToAddCartBtn = (): void => {
  const button = document.querySelector(`._add-to-cart.gtm_add_cart_btn_${productRef.value?.slug}`)
  if (button) {
    button.scrollIntoView({ behavior: "smooth" })
  }
}

// ラッピング確定
const wrappingConfirm = (tempSelectionGift: CartGift, isCard: boolean): void => {
  if (!isCard && !tempSelectionGift.sku_id) {
    toast?.showErrorToasted("ラッピングを選択して下さい")
    return
  } else if (isCard && !tempSelectionGift.card_sku_id) {
    toast?.showErrorToasted("カードを選択して下さい")
    return
  }
  isConfirm.value = true
  state.selectedGift = tempSelectionGift
  const giftIndex =
    (giftWrappingImageIndex.value < 0 ? state.selectedSku?.sub_images.length : giftWrappingImageIndex.value) ?? -1
  if (!isCard && giftIndex >= 0) {
    // ギフト資材画像にスライド
    topImagePath.value = slideImages.value[giftIndex]
  }
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
  if (isFromEGift.value) openEGiftModal()
  if (isFromEmbroidery.value) openEmbroidery()
  toast?.showSuccessToasted(`${isCard ? "メッセージカード" : "ギフトラッピング"}の設定を完了しました`)
}

const eGiftConfirm = (): void => {
  if (store.eGift.message === "" || store.eGift.giver_name === "") {
    toast?.showErrorToasted("メッセージ・贈り主を入力して下さい")
    return
  }
  if (!store.eGift.is_select && !isCartEmpty.value) {
    toast?.showErrorToasted("通常商品とは同時購入できません。eギフトではない商品をカートから削除してください")
    return
  }

  store.eGift.is_select = true
  store.setEGift(store.eGift)
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
  toast?.showSuccessToasted("eギフトの設定を完了しました")
  if (isMobile) ScrollToAddCartBtn()
}

const eGiftLift = (): void => {
  store.resetEGift()
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
  toast?.showSuccessToasted("eギフトの設定を解除しました")
}

const eGiftContentsClose = (): void => {
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const embroideryContentsClose = (): void => {
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const sizeCheckContentsClose = (): void => {
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const sizeCheckContentsOpen = async (
  gender: RecommendSizeChartGender,
  height?: number,
  weight?: number,
  foot_size?: RecommendSizeCharFootSize,
): Promise<void> => {
  try {
    isSizeCheckLoading.value = true
    const { results, errorMessage } = await fetchRecommendSizeChart(
      productRef.value?.document_id,
      gender,
      isFootCategory,
      height,
      weight,
      foot_size,
    )
    state.sizeChart = results
    if (errorMessage) {
      toast?.showErrorToasted(errorMessage)
      return
    }
    trackSizeRecommendClick({
      product_id: productRef.value?.document_id ?? "",
      height,
      weight,
      gender,
      foot_size,
      recommend_size: getRecommendSize.value,
    })
  } catch (e) {
    toast?.showErrorToasted("推奨サイズの取得に失敗しました")
  } finally {
    isSizeCheckLoading.value = false
  }
}

const isOutOfStock = (sku: SkuFragmentFragment | null = state.selectedSku) => {
  return sku ? isSkuOutOfStock(sku) : true
}

const onFavorite = async (): Promise<void> => {
  const isSignedIn = await signInCheck()
  if (!isSignedIn) return
  const skuId = state.selectedSku?.document_id || ""
  const productId = props.product?.document_id || ""
  state.favorite = await onFavoriteByCart(state.favorite, skuId, productId)
}

const skusIsOutOfStock = computed(() => {
  return productRef.value?.skus?.some((sku) => isSkuOutOfStock(sku))
})

const selectedSkuSize = computed(() => {
  if (state.selectedSku?.size_supplement) return state.selectedSku.size_supplement
  if (state.selectedSku?.size) return state.selectedSize || "未選択"
})

const selectedSkuShippingSupplement = computed<string>(() => {
  return state.selectedSku?.shipping_supplement ?? ""
})

const outOfStockSkuShippingSupplement = computed<string>(() => {
  if (!state.selectedSku?.is_restock_notice)
    return state.selectedSku?.is_optional_shipping_supplement
      ? selectedSkuShippingSupplement.value
      : "次回入荷予定はありません"
  return selectedSkuShippingSupplement.value
})

const selectedSkuSizeLabel = computed(() => {
  if (state.selectedSku?.size_label) return state.selectedSku.size_label
  return "サイズ"
})

const selectedSkuColorLabel = computed(() => {
  if (state.selectedSku?.color_label) return state.selectedSku.color_label
  return "カラー選択"
})

const replaceCartList = async (): Promise<void> => {
  if (!state.selectedSku) return
  const cartItem = {
    product_id: state.selectedSku.product_id,
    product_name: productRef.value?.name,
    sku_id: state.selectedSku.document_id,
    site_id: state.selectedSku.site_id || undefined,
    sku_name: state.selectedSku.name,
    is_reservation: state.selectedSku.is_reservation || false,
    material: productRef.value?.material || undefined,
    net_stock_quantity: state.selectedSku.net_stock_quantity || 0,
    net_stock_quantity_limit: state.selectedSku.net_stock_quantity_limit || 0,
    gift: (state.selectedGift.sku_id || state.selectedGift.card_sku_id) && isConfirm ? state.selectedGift : undefined,
    is_bundled_wrapping: productRef.value?.is_gift || productRef.value.cart_only_gift,
    is_wrapping_product: productRef.value?.is_wrapping,
    wrapping_sku_id: productRef.value?.wrapping_sku_id,
    embroidery: isEmbroideryConfirm.value && state.selectEmbroidery.text !== "" ? state.selectEmbroidery : undefined,
    is_duvet_collection: productRef.value?.is_duvet_collection,
  }
  const cartItemsArray = Array.from({ length: quantityRef.value }, () => cartItem)
  await addCartMultiItem({ cartItems: cartItemsArray, skuIds: [state.selectedSku.document_id] })
}

const slideImages = computed<string[]>(() => {
  if (state.displayedSku) {
    const images = state.displayedSku.main_image ? [state.displayedSku.main_image] : []

    state.displayedSku.sub_images?.forEach((subImage) => {
      if (subImage?.image_url) images.push(subImage.image_url)
    })
    // 専用ラッピング画像がないかつラッピング済み商品以外の場合は、選択可能な資材画像を追加
    if (giftWrappingImageIndex.value < 0 && !productRef.value?.is_wrapping) {
      if (isConfirm.value && state.selectedGift.sku_id) {
        // ラッピング選択時のギフト資材画像→選択中の資材画像を追加
        const giftSku = giftWrappings.value.find((sku) => {
          return sku.wrappingSku.document_id === state.selectedGift.sku_id
        })
        if (giftSku?.img) images.push(giftSku.img)
      } else {
        // ラッピング未選択時のギフト資材画像→選択可能な全資材画像を追加
        giftWrappings.value.forEach((gift) => {
          if (!gift.isOutOfStock && gift.wrappingSku.main_image) images.push(gift.wrappingSku.main_image)
        })
      }
    }
    return images
  }
  return []
})

const changeSelectedSku: EventHandler = (_sku: SkuFragmentFragment, isColor: boolean) => {
  state.displayedSku = _sku
  state.selectedSku = _sku
  state.selectedDisplayStockSku = _sku
  if (!isColor) state.selectedSize = _sku.size
  if (state.selectedSizeErrorMessage) state.selectedSizeErrorMessage = ""
}

const selectSizeValidation = (): void => {
  if (store.eGift.is_select) return
  if (!state.selectedSize) {
    state.selectedSizeErrorMessage = "サイズを選択してください"
    const element = sizeElementRef.value
    if (element)
      element.scrollIntoView({
        behavior: "smooth",
      })
    return
  }
  state.selectedSizeErrorMessage = ""
}

const changeDisplayStockSelectedSku = async (sku: SkuFragmentFragment): Promise<void> => {
  state.selectedDisplayStockSku = sku
  await initializeStockVariablesAndFetchStocks()
}

const resetModalVisible = (): void => {
  displayGiftRef.value = false
  displayEGiftRef.value = false
  displayEGiftExplanationRef.value = false
  displayEmbroideryRef.value = false
  displayEmbroideryExplanationRef.value = false
  displaySizeCheckRef.value = false
  isFromEGift.value = false
  isFromEmbroidery.value = false

  modalState.value.topImage.show = false
  modalState.value.displayStoreStock = false
  modalState.value.sizeChart = false
}

const openGiftModal = async (fromEGift?: boolean, fromEmbroidery?: boolean): Promise<void> => {
  isConfirm.value = false
  resetModalVisible()
  state.selectedGift = SELECT_GIFT_INIT_DATA
  if (fromEGift) isFromEGift.value = true
  if (fromEmbroidery) isFromEmbroidery.value = true
  await fetchGiftPattern() // 取得回数が多くなる場合は都度取得しないようにする
  displayGiftRef.value = true
}

const openSelectGiftModal = async (): Promise<void> => {
  isConfirm.value = false
  resetModalVisible()
  state.selectedGift = SELECT_GIFT_INIT_DATA
  displayGiftRef.value = true
  await fetchGiftPattern() // 取得回数が多くなる場合は都度取得しないようにする
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const openEGiftModal = (): void => {
  resetModalVisible()
  displayEGiftRef.value = true
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const initializeStockVariablesAndFetchStocks = async (): Promise<void> => {
  if (!state.selectedDisplayStockSku) {
    state.selectedDisplayStockSku = productRef.value.skus[0]
  }

  const response = await $fetch<ApiDataStockResponse>("/api/data/stock", {
    method: "GET",
    query: {
      managementJanCode: state.selectedDisplayStockSku?.management_jan_code ?? "",
    } satisfies ApiDataStockQuery,
  })
  const stocks = response.filter((stock) => stock.store?.type === "official" || stock.store?.type === "popup")
  state.storesDetail.forEach((store) => (store.stocks = []))
  initializationStocks(stocks, state.storesDetail)
}

const openStoreStockModal = async (): Promise<void> => {
  resetModalVisible()
  state.storesDetail.forEach((store) => {
    store.isOpen = true
  })
  modalState.value.displayStoreStock = true
  await initializeStockVariablesAndFetchStocks()
}

const openEGiftExplanation = (): void => {
  resetModalVisible()
  displayEGiftExplanationRef.value = true
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const openProductInfoModal = (): void => {
  resetModalVisible()
  emit("openProductInfoModal")
}

const displayAvailableStores = (stock: ApiDataStockResponse[number]): boolean => {
  const selectedDisplayStockSku = state.selectedDisplayStockSku
  const managementJanCodes = selectedDisplayStockSku?.management_jan_code?.split("/").filter(Boolean)
  const stockJanCode = stock.sku?.management_jan_code || stock.sku?.jan_code

  if (!stockJanCode) return false
  return managementJanCodes?.includes(stockJanCode) ?? false
}

const nextFrame = (fn: FrameRequestCallback): void => {
  window.requestAnimationFrame(() => window.requestAnimationFrame(fn))
}

const enter = (el: Element): void => {
  if (el instanceof HTMLElement) {
    el.style.overflow = "hidden"
    el.style.height = "0"
    nextFrame(() => {
      el.style.height = `${el.scrollHeight}px`
    })
  }
}

const leave = (el: Element): void => {
  if (el instanceof HTMLElement) {
    el.style.overflow = "hidden"
    el.style.height = `${el.scrollHeight}px`
    nextFrame(() => {
      el.style.height = "0"
    })
  }
}
const afterEnter = (el: Element): void => {
  if (el instanceof HTMLElement) {
    el.style.height = ""
    el.style.overflow = ""
  }
}
const afterLeave = (el: Element): void => {
  if (el instanceof HTMLElement) {
    el.style.height = ""
    el.style.overflow = ""
  }
}

const updateFilteredStocks = () => {
  state.storesDetail.forEach((store) => {
    store.filteredStocks = store.stocks.filter((stock) => displayAvailableStores(stock))
    // 店舗ごとの在庫をグループ化
    const stocksGroupedByStoreId: { [key: string]: ApiDataStockResponse } = {}

    store.filteredStocks.forEach((stock) => {
      if (stock.store?.document_id) {
        if (!stocksGroupedByStoreId[stock.store.document_id]) {
          stocksGroupedByStoreId[stock.store.document_id] = []
        }
        stocksGroupedByStoreId[stock.store.document_id].push(stock)
      }
    })

    // セットアイテムであればトップスとボトムスの在庫を識別して SetStocks オブジェクトを作成
    if (isSetItemRef.value) {
      store.filteredSetStocks = Object.values(stocksGroupedByStoreId).map((stocks) => {
        const topsStock = stocks.find((stock) => stock.sku?.product?.wear_category === "tops")
        const bottomsStock = stocks.find((stock) => stock.sku?.product?.wear_category === "bottoms")
        const store = topsStock?.store || bottomsStock?.store // どちらかの在庫が存在する場合の店舗情報

        return { topsStock, bottomsStock, store }
      })
    }
  })
}

const openEmbroidery = (): void => {
  resetModalVisible()
  displayEmbroideryRef.value = true
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const openEmbroideryExplanation = (): void => {
  resetModalVisible()
  displayEmbroideryExplanationRef.value = true
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const openSkuImageModal = (path: string): void => {
  resetModalVisible()
  modalState.value.topImage.path = path
  modalState.value.topImage.show = true
}

const openSizeChartImageModal = (): void => {
  resetModalVisible()
  modalState.value.sizeChart = true
}

const openSizeCheck = (): void => {
  if (isBodygramSupportedRef.value && bodygramWidgetRef.value) {
    bodygramWidgetRef.value.open()
    return
  }
  resetModalVisible()
  displaySizeCheckRef.value = true
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const onClickBuyNowBefore = async (): Promise<void> => {
  if (showDuvetCollectionModal.value) {
    isDuvetCollectionBuyNowModalVisible.value = true
  } else {
    await onClickBuyNow()
  }
}

const onClickBuyNow = async (): Promise<void> => {
  // カートが空であり、かつeGiftが選択されていない場合、サイズ選択のバリデーションを行う
  if (isCartEmpty.value && !store.eGift.is_select) {
    selectSizeValidation()
    if (state.selectedSizeErrorMessage) return
  }

  // カートが空の場合、選択中のSKUをカートに追加
  if (isCartEmpty.value) await replaceCartList()
  router.push("/order/new")
}

const onClickAmazonPay = async (): Promise<void> => {
  if (isCartEmpty.value) await replaceCartList()
}

const handleResize: EventHandler = () => {
  state.isSp = window.innerWidth < 768
}

const mountAmazonPayButton = async () => {
  await nextTick()
  const amazonPayClient = new AmazonPayClient({ id: amazonPayButtonId })
  if (!amazonPayClient || !document.getElementById(amazonPayButtonId)) return

  amazonPayClient.renderButton({
    placement: "Product",
    ledgerCurrency: "JPY",
    checkoutLanguage: "ja_JP",
    // process.env.NODE_ENV !== "production" だとNODE_ENV指定されてない場合にSANDBOX環境にならないため
    sandbox: !(config.public.APP_ENV === "production"),
  })
}

const onAmazonPayDuvetCollectionModalDone = () => {
  if (amazonPayButtonRef.value) {
    amazonPayButtonRef.value.click()
  }
}

/**
 * eギフト購入可能条件
 * 1. ギフトラッピングが存在する
 * 2. 予約商品でない
 * 3. 刺繍サービスが未設定
 * 4. すべてのSKUの価格が同じ
 * 5. 店頭決済が無効
 */
const eGiftConfigurable = computed(() => {
  return (
    giftWrappings.value.length &&
    !state.selectedSku?.is_reservation &&
    !isEmbroideryConfirm.value &&
    allPricesAreEqual.value &&
    visitStore.value.isExpired
  )
})

/**
 * 刺繍サービス設定可能条件
 * 1. 商品が刺繍対応商品である
 * 2. eギフトが選択されていない
 * 3. ギフトラッピング・カードが選択されておらず、ラッピング資材が在庫切れでない
 * 4. 店頭決済が無効
 */
const embroideryConfigurable = computed(() => {
  return (
    productRef.value.is_embroidery &&
    !store.eGift.is_select &&
    !selectedGiftHasCardAndMessageAndAndBundlingInclude.value &&
    visitStore.value.isExpired
  )
})

const allPricesAreEqual = computed(() => {
  const skus = props.product.skus
  if (skus.length === 0) return true

  const firstPrice = calcPrice(skus[0]).effective_price_exclude_tax
  return skus.every((sku) => sku.price === firstPrice)
})

const productSegmentList = computed(() => {
  const result = props.listParts
    .map((item) => {
      const key = item.subtitle || ""
      const value = item.product_id || ""
      return { key, value }
    })
    .filter((item) => item.value && item.key)
  result.unshift({ key: props.subtitle, value: props.product.document_id })
  return result
})

const setBodygramWidget = async () => {
  const { bodygramWidget, isBodygramSupported } = await initBody2FitWidget(props.product.slug)
  if (bodygramWidget) bodygramWidgetRef.value = bodygramWidget
  isBodygramSupportedRef.value = isBodygramSupported
}

const showDuvetCollectionModal = computed(() => {
  return showDuvetCollection() && productRef.value.is_duvet_collection && getDuvetCollectionType() === "none"
})

onMounted(async () => {
  handleResize()

  if (productRef.value?.skus?.length) {
    const inStockSkus = productRef.value.skus.filter((sku) => !isSkuOutOfStock(sku))
    const selectedSku = inStockSkus[0] ?? productRef.value.skus[0] ?? null

    state.selectedSku = selectedSku
    state.displayedSku = selectedSku
    state.selectedSize = inStockSkus.length === 1 ? selectedSku?.size || "" : ""
  }

  if (productRef.value?.is_embroidery) {
    state.selectEmbroidery.position = productRef.value.embroidery?.embroidery_positions[0] ?? ""
  }
  await mountAmazonPayButton()
  isSetItemRef.value = productRef.value?.wear_category === "set"

  // ログインしているかどうかでお気に入りの状態を取得
  if (isSigned.value) {
    await fetchFavoriteListByCart(props.product.document_id)
    state.favorite = favoriteListRef.value.find((favorite) => favorite.sku_id === state.selectedSku?.document_id)
  }

  await setBodygramWidget()
})

watch(
  () => state.selectedSku,
  () => {
    if (state.selectedGift.sku_id && isConfirm.value) {
      const isOutOfStock = giftWrappings.value.some(
        (gift) => gift.isOutOfStock && gift.wrappingSku?.product_id === state.selectedGift.product_id,
      )
      if (isOutOfStock) {
        isConfirm.value = false
        state.selectedGift = SELECT_GIFT_INIT_DATA
      }
    }
    updateFilteredStocks()
    state.favorite = favoriteListRef.value?.find((favorite) => favorite.sku_id === state.selectedSku?.document_id)
  },
)
</script>

<style module lang="scss">
.root {
  @include container-lg;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: 1.5rem;

  @include lg {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 2.5rem;
  }

  .line_container {
    padding: 1rem 0;

    &:first-child {
      padding: 0 0 1rem;
    }
    &:last-child {
      padding: 1rem 0 0;
    }
    &:not(:last-child) {
      border-bottom: 1px solid $primary-20;
    }
  }

  .detail {
    @include lg {
      flex-shrink: 0;
      min-width: 350px;
      width: 50%;
      max-width: 600px;
    }

    .restock_notice {
      color: $primary;
      font-size: $font-size-14;
    }

    .color_with_size {
      display: flex;
      flex-direction: column;
      gap: 1.125rem;

      .size_container {
        display: flex;
        flex-direction: column;
        gap: 0.875rem;
        a,
        p {
          font-size: $font-size-12;
          @include lg {
            font-size: $font-size-14;
          }
        }
        .label {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.5rem;
          .left {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
          }
          .link {
            cursor: pointer;
            text-decoration: underline;
            margin-left: auto;
          }
        }
      }
    }

    .out_of_stock_label {
      margin-top: 1.25rem;
      font-size: $font-size-14;
    }

    .delivery_date_container {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;

      .delivery_icon {
        margin-top: 0.5rem;
      }

      p {
        font-size: $font-size-12;
        @include lg {
          font-size: $font-size-14;
        }
      }

      .select_container {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .select {
          width: 100px;

          :global {
            select {
              font-size: $font-size-12;
              @include lg {
                font-size: $font-size-14;
              }
            }
          }
        }
      }

      .tips {
        font-size: $font-size-10;
        @include lg {
          font-size: $font-size-12;
        }
      }
    }

    .cart_button_container {
      display: flex;
      flex-direction: column;
      .option_container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 1rem;

        p {
          font-size: $font-size-12;
          @include lg {
            font-size: $font-size-14;
          }
        }

        .buttons {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          .tips {
            font-size: $font-size-10;
            @include lg {
              font-size: $font-size-12;
            }
          }
          &.wrapping_buttons {
            @include lg {
              flex-direction: row;
              gap: 0.5rem;
              .e_gift_button {
                max-width: 200px;
              }
            }
          }
        }

        .tips {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          .row {
            @mixin row_font {
              font-size: $font-size-10;
              @include lg {
                font-size: $font-size-12;
              }
            }

            display: flex;
            @include row_font;

            a {
              @include row_font;
              text-decoration: underline;
              cursor: pointer;
            }
          }
        }
      }

      .buttons {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        .buy_now_with_amazon_pay {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;

          .buy_now_button {
            background-color: $secondary;
          }

          button {
            display: flex;
            justify-content: center;
          }
        }
      }

      .description {
        :global {
          * {
            font-size: $font-size-10;
            @include lg {
              font-size: $font-size-12;
            }
          }
        }
      }
    }
    .option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      .stock_modal_button {
        flex-grow: 1;
      }
      .fav {
        flex-shrink: 0;
        display: flex;
        justify-content: center;
        align-self: center;
        padding-left: 0.75rem;
        padding-right: 0.75rem;
      }
    }
  }

  .image_modal {
    @include lg {
      max-width: $max-width-md;
    }
    img,
    video {
      min-height: 300px;
      min-width: 300px;
      width: 100%;
      object-fit: contain;
    }
    .img {
      width: 100%;
      transition: all 0.3s ease;
      &.zoom {
        width: 200%;
        padding-right: 1rem;
      }
    }
  }

  .amazon_pay {
    position: relative;
    .disabled {
      pointer-events: none;
    }
    .cover {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      cursor: pointer;
    }
  }
  .error_message {
    margin-top: 0.5rem;
    font-size: 12px;
    font-weight: bold;
    color: $danger;
  }

  // シンプルLPの場合、最大幅が720pxになるのでPCのスタイルを効かせないようにする
  &.lpnh {
    @include full-container-lg;
    padding: 0 1.5rem;
    @include lg {
      & {
        flex-direction: column;
        gap: 1.5rem;
      }
      .top_image {
        width: 100%;
      }
      .detail {
        width: 100%;
      }
    }
  }
}
</style>

<style>
/* 
Body2Fit
ヘッダーや追従カートボタンに被らないようにするためz-indexを設定
*/
.b2f-container {
  z-index: 1000;
}
</style>
